import { ALL_TIKTOK_SCOPES } from "../../../platform/tiktok/scopes";

/**
 * TikTok Marketing API permission scopes.
 *
 * TikTok identifies scopes by numeric id in a three-level tree: a first-level
 * group ("6" Creative Management), a second-level area ("61" Video
 * Management) and a third-level operation ("610" Read Video Library). A token
 * may report any mix of levels, and which level it reports depends on what the
 * advertiser ticked in the consent screen, so a check has to walk the tree
 * rather than compare strings. {@link tiktokGrants} is the only place that
 * walk happens.
 */

export const READ_ONLY_TIKTOK_SCOPES = [
  "4", // Reporting: campaign and ad performance data
];

export const MINIMAL_TIKTOK_SCOPES = [
  "2", // Ads Management: campaign, ad group and ad CRUD
  "6", // Creative Management: video and image uploads, creative tools
  "8", // Pixel Management: tracking pixels
];

export const TIKTOK_SCOPES: string[] = [
  ...MINIMAL_TIKTOK_SCOPES,
  "3", // Audience Management: custom audiences
  "4", // Reporting: campaign performance data
  "6110000", // Creative Recommendation (parent of CTA and Smart Text)
];

export const TIKTOK_ADVANCED_CREATIVE_SCOPES = [
  "6110100", // CTA Recommendation: /creative/cta/recommend/, /creative/portfolio/create/
  "6110200", // Smart Text: /creative/smart_text/generate/
];

const LEVEL_BY_ID = new Map<string, number>();
const NAME_BY_ID = new Map<string, string>();
for (const scope of ALL_TIKTOK_SCOPES) {
  LEVEL_BY_ID.set(scope.id, scope.level);
  if (!NAME_BY_ID.has(scope.id)) NAME_BY_ID.set(scope.id, scope.name);
}

/**
 * Ids that are 64-bit snowflakes rather than structured numbers. JSON.parse
 * rounds them to the nearest double ("7224195155857866754" becomes
 * 7224195155857867000), so a stored id that is not in the catalog is matched
 * back to the catalog id that rounds to the same double.
 */
const SNOWFLAKE_BY_DOUBLE = new Map<number, string>();
for (const id of LEVEL_BY_ID.keys()) {
  if (id.length > 15) SNOWFLAKE_BY_DOUBLE.set(Number(id), id);
}

/**
 * The parent of a structured scope id, derived from how TikTok numbers them:
 * short ids drop their last digit ("600" → "60" → "6"), and seven or eight
 * digit ids zero their trailing digit pairs ("6110100" → "6110000" → "6",
 * "18030100" → "18030000" → "18000000"). A candidate only counts when the
 * catalog has it one level up.
 *
 * Snowflake ids (the long ones TikTok added later) carry no structure, so
 * they have no known parent: only the exact id, or a held descendant of it,
 * grants them.
 */
function parentOf(id: string): string | null {
  const level = LEVEL_BY_ID.get(id);
  if (level === undefined || level <= 1 || id.length > 15) return null;
  const candidates: string[] = [];
  if (id.length <= 6) candidates.push(id.slice(0, -1));
  if (id.length === 7 || id.length === 8) {
    for (const zeros of [4, 6]) {
      const zeroed = id.slice(0, id.length - zeros) + "0".repeat(zeros);
      candidates.push(zeroed, zeroed.replace(/0+$/, ""));
    }
  }
  return (
    candidates.find((candidate) => LEVEL_BY_ID.get(candidate) === level - 1) ??
    null
  );
}

function ancestorsOf(id: string): string[] {
  const ancestors: string[] = [];
  let current = parentOf(id);
  while (current) {
    ancestors.push(current);
    current = parentOf(current);
  }
  return ancestors;
}

/**
 * Repairs one stored TikTok scope id: trims it, and maps a snowflake that
 * lost precision on its way through JSON.parse back to its catalog id.
 */
export function normalizeTikTokScopeId(raw: string | number): string {
  const text = String(raw).trim();
  if (LEVEL_BY_ID.has(text)) return text;
  const asNumber = Number(text);
  if (Number.isFinite(asNumber) && asNumber > Number.MAX_SAFE_INTEGER) {
    return SNOWFLAKE_BY_DOUBLE.get(asNumber) ?? text;
  }
  return text;
}

export function normalizeTikTokScopeIds(
  raw: ReadonlyArray<string | number>,
): string[] {
  return [...new Set(raw.map(normalizeTikTokScopeId))];
}

/**
 * Whether the held scopes grant `required`.
 *
 * - The exact id, or any ancestor of it, grants it: holding "6" covers "610".
 * - Any descendant grants it too. TikTok reports the leaves an advertiser
 *   ticked ("600" Read Image Library) rather than the group, and the older
 *   hand-written check already accepted a child for its group. That rule is
 *   kept, now for every id in the catalog instead of a short hand list that
 *   missed "600" and flagged working connections as missing scopes.
 */
export function tiktokGrants(
  held: ReadonlyArray<string | number>,
  required: string,
): boolean {
  const heldIds = normalizeTikTokScopeIds(held);
  if (heldIds.length === 0) return false;
  const heldSet = new Set(heldIds);
  if (heldSet.has(required)) return true;
  if (ancestorsOf(required).some((ancestor) => heldSet.has(ancestor))) {
    return true;
  }
  return heldIds.some((id) => ancestorsOf(id).includes(required));
}

export function tiktokScopeName(id: string): string {
  return NAME_BY_ID.get(id) ?? `scope ${id}`;
}

/**
 * The scope array from TikTok's `/oauth2/access_token/` body, as strings.
 *
 * Read from the raw text because the body carries snowflake scope ids as bare
 * JSON numbers, which JSON.parse rounds. Every id inside `"scope": [...]` is
 * quoted before parsing so it survives exactly.
 */
export function quoteTikTokScopeIdsInJson(rawBody: string): string {
  return rawBody.replace(
    /("scope"\s*:\s*\[)([^\]]*)(\])/g,
    (_match, open: string, items: string, close: string) =>
      `${open}${items.replace(/(^|,)\s*(\d+)\s*(?=,|$)/g, '$1"$2"')}${close}`,
  );
}

/**
 * Parses TikTok's `/oauth2/access_token/` body with scope ids kept exact.
 *
 * Assumes the scope list is a flat JSON array of bare integers (or strings)
 * under the key "scope". If TikTok ever changes that shape and the rewritten
 * text no longer parses, the original body is parsed as-is instead: a
 * rounded snowflake id is repaired later by normalizeTikTokScopeIds, while a
 * thrown parse error would fail the whole OAuth completion. A body that is
 * not JSON at all comes back unchanged, as axios would leave it.
 */
export function parseTikTokOAuthBody<T>(rawBody: string): T | string {
  try {
    const quoted: T = JSON.parse(quoteTikTokScopeIdsInJson(rawBody));
    return quoted;
  } catch {
    try {
      const original: T = JSON.parse(rawBody);
      return original;
    } catch {
      return rawBody;
    }
  }
}
